import {
	CommandDialog,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from "@/components/ui/command";
import { useEffect } from "react";
import { AgentAvatar } from "./AgentAvatar.js";
import { PlatformMark } from "./PlatformMark.js";
import {
	AGENTS,
	COMMS_CHANNELS,
	CONNECTORS,
	type AgentId,
	type Integration,
	type PrimaryNav,
} from "./nav.js";
import {
	stubPendingApprovals,
	type PendingApproval,
} from "./sidebar-stubs.js";
import {
	ActivityIcon,
	DollarSignIcon,
	GlobeIcon,
	UsersIcon,
} from "lucide-react";

type Props = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	setupDone: boolean;
	onOpenAgent: (id: AgentId) => void;
	onOpenIntegration: (item: Integration, kind: "connector" | "channel") => void;
	onOpenNav: (nav: PrimaryNav) => void;
	onOpenTeam: () => void;
	onOpenBilling: () => void;
	onOpenActivity: () => void;
	onOpenApproval?: (approval: PendingApproval) => void;
};

/** ⌘K jump palette — agents, connectors, channels, approvals, actions. */
export function CommandPalette({
	open,
	onOpenChange,
	setupDone,
	onOpenAgent,
	onOpenIntegration,
	onOpenNav,
	onOpenTeam,
	onOpenBilling,
	onOpenActivity,
	onOpenApproval,
}: Props) {
	const approvals = stubPendingApprovals(setupDone);
	const agents = AGENTS.filter(
		(a) =>
			a.availability === "live" ||
			(!setupDone && a.id === "onboarding"),
	);

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			const mod = event.metaKey || event.ctrlKey;
			if (!mod || event.altKey || event.shiftKey) return;
			if (event.key.toLowerCase() !== "k") return;
			event.preventDefault();
			onOpenChange(!open);
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [open, onOpenChange]);

	return (
		<CommandDialog
			open={open}
			onOpenChange={onOpenChange}
			title="Search"
			description="Jump to an agent, connector, channel, or action."
		>
			<CommandInput placeholder="Search agents, connectors, channels…" />
			<CommandList>
				<CommandEmpty>No matches.</CommandEmpty>

				<CommandGroup heading="Team">
					{agents.map((agent) => {
						const locked =
							!setupDone && agent.id !== "onboarding";
						return (
							<CommandItem
								key={agent.id}
								disabled={locked}
								value={`${agent.name} ${agent.department} ${agent.title}`}
								onSelect={() => {
									onOpenChange(false);
									onOpenAgent(agent.id);
								}}
							>
								<AgentAvatar agent={agent} size="sm" />
								<span className="min-w-0 flex-1 truncate">
									{agent.name}
									<span className="ml-1.5 text-muted-foreground">
										{agent.department}
									</span>
								</span>
							</CommandItem>
						);
					})}
					<CommandItem
						value="your team all agents roster"
						onSelect={() => {
							onOpenChange(false);
							onOpenTeam();
						}}
					>
						<UsersIcon className="size-4" />
						Your team
					</CommandItem>
				</CommandGroup>

				{approvals.length > 0 && (
					<>
						<CommandSeparator />
						<CommandGroup heading="Needs you">
							{approvals.map((item) => {
								const agent = AGENTS.find(
									(a) => a.id === item.agentId,
								);
								return (
									<CommandItem
										key={item.id}
										value={`approval ${item.summary} ${agent?.name ?? ""}`}
										onSelect={() => {
											onOpenChange(false);
											if (onOpenApproval) {
												onOpenApproval(item);
											} else {
												onOpenAgent(item.agentId);
											}
										}}
									>
										{agent ? (
											<AgentAvatar agent={agent} size="sm" />
										) : null}
										<span className="line-clamp-1">
											{item.summary}
										</span>
									</CommandItem>
								);
							})}
						</CommandGroup>
					</>
				)}

				{setupDone && (
					<>
						<CommandSeparator />
						<CommandGroup heading="Connectors">
							{CONNECTORS.map((item) => (
								<CommandItem
									key={item.id}
									value={`connector ${item.name} ${item.blurb}`}
									onSelect={() => {
										onOpenChange(false);
										// openIntegration sets nav/surface — do not call
										// onOpenNav first (it clears selectedIntegration).
										onOpenIntegration(item, "connector");
									}}
								>
									<span className="grid size-6 place-items-center">
										<PlatformMark
											markId={item.markId}
											size={16}
											fallback={item.name}
										/>
									</span>
									{item.name}
									{item.status === "soon" && (
										<span className="ml-auto text-[10px] text-muted-foreground">
											Soon
										</span>
									)}
								</CommandItem>
							))}
						</CommandGroup>
						<CommandSeparator />
						<CommandGroup heading="Websites">
							<CommandItem
								value="websites sites hosted domains forms"
								onSelect={() => {
									onOpenChange(false);
									onOpenNav("sites");
								}}
							>
								<GlobeIcon className="size-4" />
								Websites
							</CommandItem>
						</CommandGroup>
						<CommandSeparator />
						<CommandGroup heading="Channels">
							{COMMS_CHANNELS.map((item) => (
								<CommandItem
									key={item.id}
									value={`channel ${item.name} ${item.blurb}`}
									onSelect={() => {
										onOpenChange(false);
										onOpenIntegration(item, "channel");
									}}
								>
									<span className="grid size-6 place-items-center">
										<PlatformMark
											markId={item.markId}
											size={16}
											fallback={item.name}
										/>
									</span>
									{item.name}
									{item.status === "soon" && (
										<span className="ml-auto text-[10px] text-muted-foreground">
											Soon
										</span>
									)}
								</CommandItem>
							))}
						</CommandGroup>
					</>
				)}

				<CommandSeparator />
				<CommandGroup heading="Actions">
					<CommandItem
						value="live activity running waiting"
						onSelect={() => {
							onOpenChange(false);
							onOpenActivity();
						}}
					>
						<ActivityIcon className="size-4" />
						Live activity
					</CommandItem>
					<CommandItem
						value="billing usage spend"
						onSelect={() => {
							onOpenChange(false);
							onOpenBilling();
						}}
					>
						<DollarSignIcon className="size-4" />
						Usage & billing
					</CommandItem>
				</CommandGroup>
			</CommandList>
		</CommandDialog>
	);
}
